import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText, Loader2, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "../App";

export default function ExportButtons({ userRole, userDesignation }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(null);
  const [activeJobs, setActiveJobs] = useState({});
  const pollingIntervals = useRef({});

  // Super authorities who can export reports (Revenue: tahsildar, Survey: ddlr, adlr)
  const EXPORT_ALLOWED_DESIGNATIONS = ["tahsildar", "ddlr", "adlr"];
  const isSuperAuthority = EXPORT_ALLOWED_DESIGNATIONS.includes(userDesignation);

  // Clean up polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervals.current).forEach(clearInterval);
    };
  }, []);

  // Only show export button for Super Authorities
  if (!isSuperAuthority) {
    return null;
  }

  // Poll job status
  const pollJobStatus = async (jobId, exportType, format) => {
    try {
      const response = await api.get(`/export/status/${jobId}`);
      const job = response.data;
      
      setActiveJobs(prev => ({
        ...prev,
        [jobId]: { ...job, exportType, format }
      }));

      if (job.status === "completed") {
        // Stop polling
        if (pollingIntervals.current[jobId]) {
          clearInterval(pollingIntervals.current[jobId]);
          delete pollingIntervals.current[jobId];
        }
        
        setLoading(null);
        toast.dismiss(`export-processing-${jobId}`);
        
        // Show success toast with download button
        toast.success(
          <div className="flex flex-col gap-2">
            <span>{t("export.exportReady") || "Your export is ready!"}</span>
            <button
              onClick={() => downloadExport(jobId, job.filename)}
              className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm hover:bg-emerald-700 transition-colors w-full"
              data-testid={`download-export-${jobId}`}
            >
              {t("export.downloadNow") || "Download Now"}
            </button>
          </div>,
          {
            duration: 60000, // Show for 60 seconds
            id: `export-ready-${jobId}`
          }
        );
      } else if (job.status === "failed") {
        // Stop polling
        if (pollingIntervals.current[jobId]) {
          clearInterval(pollingIntervals.current[jobId]);
          delete pollingIntervals.current[jobId];
        }
        
        setLoading(null);
        toast.dismiss(`export-processing-${jobId}`);
        toast.error(job.error || t("export.exportFailed") || "Export failed. Please try again.");
        
        // Remove from active jobs
        setTimeout(() => {
          setActiveJobs(prev => {
            const next = { ...prev };
            delete next[jobId];
            return next;
          });
        }, 3000);
      }
    } catch (error) {
      console.error("Failed to poll job status:", error);
    }
  };

  // Download completed export
  const downloadExport = async (jobId, filename) => {
    try {
      toast.dismiss(`export-ready-${jobId}`);
      toast.loading(t("export.downloading") || "Downloading...", { id: `download-${jobId}` });
      
      const response = await api.get(`/export/download/${jobId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "export.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss(`download-${jobId}`);
      toast.success(t("export.downloadSuccess") || "Download completed!");

      // Remove from active jobs
      setActiveJobs(prev => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    } catch (error) {
      console.error("Failed to download export:", error);
      toast.dismiss(`download-${jobId}`);
      toast.error(t("export.downloadFailed") || "Failed to download. Please try again.");
    }
  };

  // Start async export (for PDF and Excel of large reports)
  const startAsyncExport = async (exportType, format = "pdf") => {
    const loadingKey = `${exportType}-${format}-async`;
    setLoading(loadingKey);
    
    try {
      const endpointMap = {
        "applications-pdf": "/export/applications/pdf/async",
        "applications-excel": "/export/applications/excel/async",
        "location-pdf": "/export/location-report/pdf/async",
        "location-excel": "/export/location-report/excel/async",
      };

      const endpoint = endpointMap[`${exportType}-${format}`];
      if (!endpoint) {
        // Fall back to sync export for unsupported types
        return handleSyncExport(exportType, format);
      }

      toast.info(t("export.starting") || "Starting export...");
      
      const response = await api.post(endpoint);
      const { job_id, status, message } = response.data;
      
      if (status === "started") {
        setActiveJobs(prev => ({
          ...prev,
          [job_id]: {
            job_id,
            exportType,
            format,
            status: "processing",
            progress: 0,
            message: message || "Processing..."
          }
        }));

        toast.loading(
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 animate-pulse" />
            <span>{t("export.generatingReport") || "Generating report... This may take a moment."}</span>
          </div>,
          { id: `export-processing-${job_id}`, duration: 120000 }
        );

        // Start polling every 2 seconds
        pollingIntervals.current[job_id] = setInterval(() => {
          pollJobStatus(job_id, exportType, format);
        }, 2000);

        // Poll immediately
        pollJobStatus(job_id, exportType, format);
        
        return job_id;
      }
      
      return null;
    } catch (error) {
      console.error("Failed to start async export:", error);
      setLoading(null);
      
      if (error.response?.status === 403) {
        toast.error(t("export.accessDenied") || "Access denied. Only Super Authorities can export reports.");
      } else if (error.response?.status === 401) {
        toast.error(t("export.unauthorized") || "Please login again to export reports.");
      } else {
        toast.error(t("export.startFailed") || "Failed to start export. Please try again.");
      }
      return null;
    }
  };

  // Sync export (for analytics and workload - smaller reports)
  const handleSyncExport = async (exportType, format = "excel") => {
    const loadingKey = `${exportType}-${format}`;
    setLoading(loadingKey);
    try {
      const endpoints = {
        applications: format === "pdf" ? "/export/applications/pdf" : "/export/applications",
        analytics: format === "pdf" ? "/export/analytics/pdf" : "/export/analytics",
        workload: format === "pdf" ? "/export/workload/pdf" : "/export/workload",
        location: format === "pdf" ? "/export/location-report/pdf" : "/export/location-report",
      };

      toast.info(t("export.preparing") || "Preparing download...");
      
      const response = await api.get(endpoints[exportType], {
        responseType: "blob",
      });

      if (!response.data || response.data.size === 0) {
        throw new Error("Empty response received");
      }

      const mimeType = format === "pdf" 
        ? "application/pdf" 
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const contentDisposition = response.headers["content-disposition"];
      const extension = format === "pdf" ? "pdf" : "xlsx";
      let filename = `${exportType}_report_${new Date().toISOString().slice(0,10)}.${extension}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t("export.downloadSuccess") || "Download completed!");
    } catch (error) {
      console.error("Export error:", error);
      if (error.response?.status === 403) {
        toast.error(t("export.accessDenied") || "Access denied. Only Super Authorities can export reports.");
      } else if (error.response?.status === 401) {
        toast.error(t("export.unauthorized") || "Please login again to download reports.");
      } else {
        toast.error(t("export.downloadFailed") || `Failed to download ${exportType} report. Please try again.`);
      }
    } finally {
      setLoading(null);
    }
  };

  // Handle export based on type and format
  const handleExport = async (exportType, format = "excel") => {
    // Use async export for both PDF and Excel of large reports (applications, location)
    if (exportType === "applications" || exportType === "location") {
      return startAsyncExport(exportType, format);
    }
    // Use sync export for smaller reports (analytics, workload)
    return handleSyncExport(exportType, format);
  };

  const ExportSubMenu = ({ reportType, label, testId }) => {
    const isAsyncExport = reportType === "applications" || reportType === "location";
    
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger 
          className="flex items-center"
          data-testid={`${testId}-trigger`}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          {label}
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              onClick={() => handleExport(reportType, "excel")}
              disabled={loading === `${reportType}-excel` || loading === `${reportType}-excel-async`}
              data-testid={`${testId}-excel`}
            >
              {(loading === `${reportType}-excel` || loading === `${reportType}-excel-async`) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
              )}
              {t("export.excel")}
              {isAsyncExport && (
                <span className="ml-1 text-xs text-slate-400">
                  ({t("export.async") || "async"})
                </span>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleExport(reportType, "pdf")}
              disabled={loading === `${reportType}-pdf` || loading === `${reportType}-pdf-async`}
              data-testid={`${testId}-pdf`}
            >
              {(loading === `${reportType}-pdf` || loading === `${reportType}-pdf-async`) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2 text-red-600" />
              )}
              {t("export.pdf")}
              {isAsyncExport && (
                <span className="ml-1 text-xs text-slate-400">
                  ({t("export.async") || "async"})
                </span>
              )}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  };

  const hasActiveExports = Object.keys(activeJobs).length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`border-emerald-500 text-emerald-700 hover:bg-emerald-50 ${hasActiveExports ? 'animate-pulse' : ''}`}
          data-testid="export-dropdown-btn"
        >
          {hasActiveExports ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {t("export.exportReports")}
          {hasActiveExports && (
            <span className="ml-1 bg-emerald-600 text-white text-xs px-1.5 py-0.5 rounded-full">
              {Object.keys(activeJobs).length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-emerald-700">
          {t("export.selectReport")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <ExportSubMenu 
          reportType="applications" 
          label={t("export.applicationsList")} 
          testId="export-applications"
        />

        <ExportSubMenu 
          reportType="analytics" 
          label={t("export.analyticsSummary")} 
          testId="export-analytics"
        />

        <ExportSubMenu 
          reportType="workload" 
          label={t("export.workloadReport")} 
          testId="export-workload"
        />

        <ExportSubMenu 
          reportType="location" 
          label={t("export.locationReport")} 
          testId="export-location"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
